import React, { useState, useEffect } from "react";

// Display metadata for the 12 master goals, keyed by backend goal id. The
// backend has no displayName or icon field (confirmed with the dev — mobile
// renders its own icons in its UI layer too), so this is the web equivalent.
// Names are written naturally rather than machine-prettified from the token.
// Exported so the Active Goals card renders identically to the modal.
export const GOAL_META = {
  1:  { displayName: "Relax",                            icon: "🌿" },
  2:  { displayName: "Have fun",                         icon: "🎉" },
  3:  { displayName: "Pursue hobbies",                   icon: "🎨" },
  4:  { displayName: "Spend time with family & friends", icon: "👨‍👩‍👧" },
  5:  { displayName: "Read",                             icon: "📚" },
  6:  { displayName: "Write",                            icon: "✍️" },
  7:  { displayName: "Watch TV",                         icon: "📺" },
  8:  { displayName: "Exercise",                         icon: "🏃" },
  9:  { displayName: "Explore interests",                icon: "🔭" },
  10: { displayName: "Eat healthy",                      icon: "🥗" },
  11: { displayName: "Live long",                        icon: "💚" },
  12: { displayName: "Travel",                           icon: "✈️" },
};

// Master goal tokens → id, derived from GOAL_META's ordering contract with the
// backend list. Used by the Active Goals card to recover ids from a text-only
// view_goal response. Kept beside GOAL_META so the two cannot drift apart.
export const GOAL_TOKEN_TO_ID = {
  RELAX: 1, HAVE_FUN: 2, PURSUE_HOBBIES: 3, SPEND_TIME_FAMILY_FRIENDS: 4,
  READ: 5, WRITE: 6, WATCH_TV: 7, EXERCISE: 8, EXPLORE_INTERESTS: 9,
  EAT_HEALTHY: 10, LIVE_LONG: 11, TRAVEL: 12,
};

// Mirrors ScheduleModalWeb's shell (fixed overlay, white card, ✕, Save). Renders
// the master goal list with checkboxes and lets the user order the selected ones
// by priority with up/down. On save it hands the parent the selected goal ids in
// priority order; the parent posts them back as the inputList the backend splits.
export default function GoalKeeperModalWeb({ open, onClose, onSave, goals = [], selectedGoals = [] }) {
  // Goal identifier and label are read defensively — the master list's exact
  // field names are the backend's, so fall back through the likely ones.
  const idOf = (g, i) => String(g?.id ?? g?._id ?? g?.goalId ?? i + 1);
  const descOf = (g) => g?.description || g?.desc || "";
  // Goal `name` is a machine token (SPEND_TIME_FAMILY_FRIENDS), so prefer any
  // human-readable field the backend sends and only prettify the token form.
  // displayName leads the chain deliberately: `name` is always truthy, so a
  // chain starting with it would short-circuit and ignore displayName once the
  // backend adds it. Guarded by TOKEN_RE so this self-disables the moment
  // either displayName arrives or `name` itself becomes readable — a stopgap
  // for the raw tokens on screen today, not a substitute for displayName.
  const TOKEN_RE = /^[A-Z0-9_]+$/;
  const prettify = (s) => (TOKEN_RE.test(s || "")
    ? s.toLowerCase().split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : s);
  const nameOf = (g, id) =>
    g?.displayName || GOAL_META[id]?.displayName || g?.title || g?.label ||
    prettify(g?.name || g?.goal) || "Goal";

  // Priority order of selected ids. Seeded from the user's saved selection (in
  // its own order), keeping only ids that exist in the current master list.
  const seed = () => {
    const valid = new Set(goals.map((g, i) => idOf(g, i)));
    return (selectedGoals || []).map(String).filter((id) => valid.has(id));
  };
  const [order, setOrder] = useState(seed);
  const [saving, setSaving] = useState(false);

  // Modal stays mounted; re-seed each open so a reopen reflects the latest data.
  useEffect(() => {
    if (!open) return;
    setOrder(seed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, goals, selectedGoals]);

  if (!open) return null;

  function toggle(id) {
    setOrder((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  }
  function move(id, dir) {
    setOrder((o) => {
      const i = o.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= o.length) return o;
      const next = [...o];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!order.length) return;
    setSaving(true);
    try {
      await onSave(order);
    } finally {
      setSaving(false);
    }
  }

  // Priority rank for a selected goal (1-based), or null if unselected.
  const rankOf = (id) => {
    const i = order.indexOf(id);
    return i < 0 ? null : i + 1;
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9750, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={onClose}>
      <form onSubmit={handleSave}
        style={{ background: "#fff", borderRadius: "14px", width: "min(520px,100%)", maxHeight: "90vh", overflow: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: "14px", position: "relative" }}
        onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose}
          style={{ position: "absolute", top: "12px", right: "12px", border: "none", background: "transparent", fontSize: "20px", cursor: "pointer", color: "#666" }}>✕</button>
        <p style={{ color: "#333", fontSize: "14px", margin: "0 24px 0 0", lineHeight: 1.5 }}>
          Choose your goals and organize them by priority. Selected goals are numbered — use the arrows to reorder.
        </p>
        {goals.length === 0 && (
          <div style={{ color: "#c00", fontSize: "13px" }}>No goals were sent by the server. Please try again.</div>
        )}
        {/* The person's goals first, in their priority order, then the rest (23 Oct). */}
        {goals
          .map((g, i) => ({ g, i, id: idOf(g, i) }))
          .sort((a, b) => (rankOf(a.id) ?? Infinity) - (rankOf(b.id) ?? Infinity) || a.i - b.i)
          .map(({ g, id }) => {
          const rank = rankOf(id);
          const selected = rank !== null;
          return (
            <div key={id} style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px", borderRadius: "8px", border: `1px solid ${selected ? "#9B7FD4" : "#e5e5e5"}`, background: selected ? "#f6f2ff" : "#fafafa" }}>
              <input type="checkbox" checked={selected} onChange={() => toggle(id)} style={{ marginTop: "3px", cursor: "pointer" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "14px", fontWeight: 700, color: "#111" }}>
                  {selected && <span style={{ color: "#6B4EAA", marginRight: "6px" }}>{rank}.</span>}
                  {GOAL_META[id]?.icon && <span style={{ marginRight: "6px" }}>{GOAL_META[id].icon}</span>}
                  {nameOf(g, id)}
                </div>
                {descOf(g) && <div style={{ fontSize: "12px", color: "#666", marginTop: "2px", lineHeight: 1.4 }}>{descOf(g)}</div>}
              </div>
              {selected && (
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <button type="button" onClick={() => move(id, -1)} disabled={rank === 1}
                    style={{ border: "none", background: "transparent", cursor: rank === 1 ? "default" : "pointer", color: rank === 1 ? "#ccc" : "#6B4EAA", fontSize: "14px", lineHeight: 1 }}>▲</button>
                  <button type="button" onClick={() => move(id, 1)} disabled={rank === order.length}
                    style={{ border: "none", background: "transparent", cursor: rank === order.length ? "default" : "pointer", color: rank === order.length ? "#ccc" : "#6B4EAA", fontSize: "14px", lineHeight: 1 }}>▼</button>
                </div>
              )}
            </div>
          );
        })}
        <button type="submit" disabled={saving || order.length === 0}
          style={{ alignSelf: "center", padding: "12px 32px", background: (saving || order.length === 0) ? "#b7a7dd" : "blue", color: "#fff", border: "none", borderRadius: "10px", fontWeight: 600, cursor: (saving || order.length === 0) ? "default" : "pointer" }}>
          {saving ? "Saving…" : "Save Goals"}
        </button>
      </form>
    </div>
  );
}
